import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Content from "../../layout/content/Content";
import {
    Card,
    Badge,
    Row,
    Col,
    Button,
    Spinner,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
} from "reactstrap";
import Head from "../../layout/head/Head";
import {
    BlockBetween,
    BlockHead,
    BlockHeadContent,
    BlockTitle,
    Icon,
} from "../../components/Component";
import {
    TableColumnsType,
    Input,
    InputNumber,
    Table,
    Select,
    Timeline,
    Collapse as AntCollapse,
    Modal as AntModal,
    Flex,
    Spin,
    Popover,
    Radio,
    Space,
    Tooltip,
    message,
    Modal as AntdModal,
    Checkbox
} from "antd";
import Swal from "sweetalert2";
import inventoryCreation from "../../api/inventory/inventoryCreation";
import dayjs from "dayjs";
import CropperComponent from "../components/CropperComponent";
import SignatureModal from "../purchase-orders/SignatureModal";
import useSequence from "../../hooks/useSequence";
import {
    SettingOutlined,
    InfoCircleOutlined,
} from "@ant-design/icons";
import sequenceApis from "../../api/master/sequence";
import misc from "../../api/misc/dropDowns";
import { MaterialTaskPanel } from "./MaterialTaskPanel";
const { Panel } = AntCollapse;

const makeHandoverTaskKey = () =>
    `htask-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

// Per-line handover task allocation panel. Each row corresponds to an issue
// task (via `issueTaskId`); the user only enters the partial `handoverQty`
// for that task — no add / remove / picker (tasks are pre-determined at the
// issue stage).
const HandoverTaskPanel: React.FC<any> = ({
    tasks,
    issueTaskOptions,
    readOnly,
    onUpdateTask,
}) => {
    const rowStyle: React.CSSProperties = {
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        alignItems: "flex-end",
        padding: "10px 12px",
        border: "1px solid #f0f0f0",
        borderRadius: 6,
        marginBottom: 10,
        background: "#fafafa",
    };

    return (
        <div style={{ padding: "4px 8px 12px" }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                }}
            >
                <span style={{ fontWeight: 600 }}>Task-wise Handover</span>
            </div>

            {tasks.length === 0 ? (
                <div style={{ color: "#8c8c8c", fontStyle: "italic" }}>
                    No issue tasks available for this line.
                </div>
            ) : (
                tasks.map((task: any, index: number) => {
                    const opt = (issueTaskOptions || []).find(
                        (o: any) => o.issueTaskId === task.issueTaskId
                    );
                    const taskLabel = opt
                        ? `${opt.taskName}${opt.boqName ? ` (${opt.boqName})` : ""}`
                        : task.taskName
                            ? `${task.taskName}${task.boqName ? ` (${task.boqName})` : ""}`
                            : task.issueTaskId != null
                                ? `Issue Task #${task.issueTaskId}`
                                : "NA";
                    return (
                        <div key={task.key} style={rowStyle}>
                            <div
                                style={{
                                    fontWeight: 500,
                                    color: "#595959",
                                    minWidth: 28,
                                }}
                            >
                                #{index + 1}
                            </div>
                            <div style={{ minWidth: 260, flex: "1 1 260px" }}>
                                <div className="data-label mb-1">Issue Task</div>
                                <div className="data-value">{taskLabel}</div>
                            </div>
                            <div style={{ minWidth: 140, flex: "0 1 140px" }}>
                                <div className="data-label mb-1">Handover Qty</div>
                                {readOnly ? (
                                    <div className="data-value">
                                        {task.handoverQty ?? "NA"}
                                    </div>
                                ) : (
                                    <InputNumber
                                        min={0}
                                        step={0.001}
                                        placeholder="Qty"
                                        style={{ width: "100%" }}
                                        value={task.handoverQty}
                                        onChange={(val) =>
                                            onUpdateTask(task.key, { handoverQty: val })
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );
};

const MaterialHandoverPendingDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [sm, setSm] = useState(false);
    const [screenSize, setScreenSize] = useState(0);
    const [mobileView, setMobileView] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showSubmitBtm, setShowSubmitBtm] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [detailsData, setDetailsData] = useState<any>();
    const [handoverSignature, setHandoverSignature] = useState<any>({});
    const [showSignatureModel, setShowSignatureModel] = useState<boolean>(false);
    const [showCropModel, setShowCropModel] = useState<boolean>(false);
    const { hasSequence } = useSequence(49);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [usersList, setUsersList] = useState<any[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedQuantities, setEditedQuantities] = useState<Record<string, number>>({});
    const [touchedLines, setTouchedLines] = useState<Set<number>>(new Set());
    const [isStrictMode, setIsStrictMode] = useState<boolean>(false);
    const [approverIds, setApproverIds] = useState<any>([]);

    const [activeSequences, setActiveSequences] = useState<any[]>([]);
    const [numberingMode, setNumberingMode] = useState<'auto' | 'manual'>('auto');
    const [showNumberingModal, setShowNumberingModal] = useState(false);
    const [tempNumberingMode, setTempNumberingMode] = useState<'auto' | 'manual'>('auto');

    // Available issue tasks per line (options for the handover task picker).
    // Keyed by issueLineId. Fetched via demo API until the real endpoint lands.
    const [lineIssueTasks, setLineIssueTasks] = useState<Record<string, any[]>>(
        {}
    );
    // User-entered handover task allocations per line. Each entry:
    // { key, issueTaskId, handoverQty }. Keyed by issueLineId.
    const [lineHandoverTasks, setLineHandoverTasks] = useState<
        Record<string, any[]>
    >({});

    const updateHandoverTask = (
        lineKey: string | number,
        taskKey: string,
        changes: any
    ) =>
        setLineHandoverTasks((prev) => ({
            ...prev,
            [lineKey]: (prev[lineKey] || []).map((t) =>
                t.key === taskKey ? { ...t, ...changes } : t
            ),
        }));

    const canUpdateHandover = detailsData?.header?.issueStatus === "Approved" &&
        detailsData?.header?.handoverStatus !== "Completed";

    const siteId = detailsData?.header?.siteId;

    const fetchSequenceAccess = useCallback(async () => {
        try {
            const response = await sequenceApis.fetchSequence();
            const { data } = response;
            const today = new Date();
            const _sequenceAccess = data?.filter(
                (item: any) =>
                    item.moduleName === "Handover" &&
                    new Date(item.endDate) > today &&
                    item.status === "Active"
            );
            setActiveSequences(_sequenceAccess || []);

            // Set initial numbering mode based on available sequences
            if (_sequenceAccess && _sequenceAccess.length > 0 && siteId) {
                const siteHasSequence = _sequenceAccess.some(
                    (seq: any) => seq.siteId?.toString() === siteId?.toString()
                );
                setNumberingMode(siteHasSequence ? 'auto' : 'manual');
            } else {
                setNumberingMode('manual');
            }
        } catch (error: any) {
            console.log(error.response?.data?.message || "An error occurred");
            setActiveSequences([]);
            setNumberingMode('manual');
        }
    }, [siteId]);

    // Get current site sequence 
    const getCurrentSiteSequence = useCallback(() => {
        if (!siteId || activeSequences.length === 0) return null;

        const siteSequence = activeSequences.find(
            (seq) =>
                seq.level === "Site" &&
                seq.siteId?.toString() === siteId?.toString()
        );

        if (siteSequence) return siteSequence;

        const orgSequence = activeSequences.find(
            (seq) => seq.level === "Org"
        );

        if (orgSequence) return orgSequence;

        return null;
    }, [siteId, activeSequences]);

    const currentSiteSequence = getCurrentSiteSequence();
    const canAuto = !!siteId && !!currentSiteSequence;

    const fetchUsers = async (siteId: any) => {
        try {
            const data = await misc.fetchSiteUsers(siteId);
            if (data) {
                const usersOptions = data?.data?.map((item: any) => ({
                    label: item.userName,
                    value: item.userId.toString(),
                }));
                setUsersList(usersOptions);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (id) {
            fetchPendingMaterialDetails(id);
        }
    }, [id]);

    useEffect(() => {
        if (siteId) {
            fetchUsers(siteId);
            fetchSequenceAccess()
        } else {
            setActiveSequences([]);
        }
    }, [siteId]);

    // Load available issue-task options for each handover line. Prefers
    // anything the backend already attached to the line; falls back to the
    // demo stub so the picker stays demoable until the real endpoint lands.
    useEffect(() => {
        const lines = detailsData?.lines || [];
        if (!lines.length) return;
        let cancelled = false;
        (async () => {
            const next: Record<string, any[]> = {};
            await Promise.all(
                lines.map(async (line: any) => {
                    if (Array.isArray(line?.tasks) && line.tasks.length > 0) {
                        next[line.issueLineId] = line.tasks;
                        return;
                    }
                    try {
                        const res: any =
                            await inventoryCreation.fetchIssueLineTasksDemo(line.issueLineId);
                        if (res?.status && Array.isArray(res.data)) {
                            next[line.issueLineId] = res.data;
                        } else {
                            next[line.issueLineId] = [];
                        }
                    } catch (err) {
                        next[line.issueLineId] = [];
                    }
                })
            );
            if (!cancelled) setLineIssueTasks(next);
        })();
        return () => {
            cancelled = true;
        };
    }, [detailsData?.lines]);

    // Build one editable row per issue task attached to the line. Source from
    // materialIssueTasks (real API), falling back to lineIssueTasks options
    // (demo) if the line doesn't carry tasks. Hydrates handover qty from any
    // backend-provided line.tasks.
    useEffect(() => {
        const lines = detailsData?.lines || [];
        if (!lines.length) return;
        const seeded: Record<string, any[]> = {};
        lines.forEach((line: any) => {
            const realTasks: any[] = Array.isArray(line?.materialIssueTasks)
                ? line.materialIssueTasks
                : [];
            const optionTasks = realTasks.length
                ? realTasks
                : lineIssueTasks[line.issueLineId] || [];
            const backendTasks: any[] = Array.isArray(line?.tasks)
                ? line.tasks
                : [];
            seeded[line.issueLineId] = optionTasks.map((opt: any) => {
                const existing = backendTasks.find(
                    (t: any) => t.issueTaskId === opt.issueTaskId
                );
                return {
                    key: makeHandoverTaskKey(),
                    issueTaskId: opt.issueTaskId,
                    taskName: opt.taskName,
                    boqName: opt.boqName || opt.boqLineName,
                    handoverQty: existing?.handoverQty,
                    selected: false,
                };
            });
        });
        setLineHandoverTasks(seeded);
    }, [detailsData?.lines, lineIssueTasks]);

    // When a line has task-level allocations, the header Handover Quantity is
    // the sum of the task qtys. Keep editedQuantities in sync so the existing
    // submit logic picks up the aggregated value without special-casing tasks.
    useEffect(() => {
        if (!isEditMode) return;
        const lines = detailsData?.lines || [];
        if (!lines.length) return;
        setEditedQuantities((prev) => {
            const next = { ...prev };
            let changed = false;
            lines.forEach((line: any) => {
                const hasTaskLines =
                    Array.isArray(line?.materialIssueTasks) &&
                    line.materialIssueTasks.length > 0;
                if (!hasTaskLines) return;
                const taskSum = (lineHandoverTasks[line.issueLineId] || []).reduce(
                    (acc: number, t: any) =>
                        acc + (t?.selected && t?.handoverQty != null ? Number(t.handoverQty) : 0),
                    0
                );
                if (next[line.issueLineId] !== taskSum) {
                    next[line.issueLineId] = taskSum;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
        setTouchedLines((prev) => {
            const next = new Set(prev);
            let changed = false;
            lines.forEach((line: any) => {
                const hasTaskLines =
                    Array.isArray(line?.materialIssueTasks) &&
                    line.materialIssueTasks.length > 0;
                const taskSum = (lineHandoverTasks[line.issueLineId] || []).reduce(
                    (acc: number, t: any) =>
                        acc + (t?.selected && t?.handoverQty != null ? Number(t.handoverQty) : 0),
                    0
                );
                if (hasTaskLines && taskSum > 0 && !next.has(line.issueLineId)) {
                    next.add(line.issueLineId);
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [isEditMode, detailsData?.lines, lineHandoverTasks]);

    const fetchPendingMaterialDetails = async (id: any) => {
        setIsLoading(true);
        try {
            const response = await inventoryCreation.fetchViewMaterialDetails(id);
            const { response: _response, status } = response;
            if (status) {
                const data = _response.data;
                const isStrict = data?.header?.isStrictApproval === true || data?.header?.isStrictApproval === 1;
                setIsStrictMode(isStrict);
                setDetailsData(data);

                if (data?.approvals) {
                    const processedApprovals = data.approvals.map((approval: any) => ({
                        ...approval,
                        level: approval.level?.toLowerCase() || approval.level,
                        status: approval.status?.toLowerCase() || approval.status
                    }));

                    const ids = processedApprovals
                        ?.filter((item: any) => item?.status?.toLowerCase() !== "approved")
                        ?.map((item: any) => item?.userId);
                    setApproverIds(ids);
                }
            }
        } catch (error) {
            console.error("Error fetching details:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getColumns = () => {
        const baseColumns: TableColumnsType<any> = [
            {
                title: "Material Category",
                dataIndex: "categoryName",
                key: "categoryName",
                width: 200,
                render: (text) => text || "NA",
            },
            {
                title: "Material Name",
                dataIndex: "materialName",
                key: "materialName",
                width: 200,
                render: (text) => text || "NA",
            },
            {
                title: "UOM",
                dataIndex: "uomName",
                key: "uomName",
                width: 200,
                render: (text) => text || "NA",
            },
            {
                title: "Issued Quantity",
                dataIndex: "quantityIssued",
                key: "quantityIssued",
                width: 200,
                render: (text, record) => (
                    <span>{Number(record.quantityIssued || record.issuedQuantity || 0).toFixed(6) || "NA"}</span>
                ),
            },
            {
                title: "Handover Status",
                dataIndex: "handoverStatus",
                key: "handoverStatus",
                width: 200,
                render: (text) =>
                    text || detailsData?.header?.handoverStatus || "NA",
            },
            {
                title: "Handovered Quantity",
                dataIndex: "quantityHandovered",
                key: "quantityHandovered",
                width: 200,
                render: (text) => <span>{Number(text || 0)}</span>,
            },
            ...(isEditMode
                ? [
                    {
                        title: "Handover Quantity",
                        key: "handoverQty",
                        width: 200,
                        render: (_: any, record: any) => {
                            const issuedQty = Number(record.quantityIssued || 0);
                            const alreadyHandovered = Number(record.quantityHandovered || 0);
                            const hasTaskLines =
                                Array.isArray(record?.materialIssueTasks) &&
                                record.materialIssueTasks.length > 0;
                            const taskSum = (
                                lineHandoverTasks[record.issueLineId] || []
                            ).reduce(
                                (acc: number, t: any) =>
                                    acc + (t?.handoverQty != null ? Number(t.handoverQty) : 0),
                                0
                            );
                            const value = hasTaskLines
                                ? taskSum
                                : editedQuantities[record.issueLineId] ?? 0;

                            return (
                                <InputNumber
                                    min={0}
                                    step={0.001}
                                    value={value}
                                    style={{ width: "100%" }}
                                    disabled={hasTaskLines}
                                    onChange={(val) => {
                                        if (val === null) return;

                                        if (val <= 0) {
                                            Swal.fire({
                                                icon: "warning",
                                                title: "Invalid Quantity",
                                                text: "Handover quantity must be greater than 0",
                                            });
                                            return;
                                        }

                                        if (alreadyHandovered + val > issuedQty) {
                                            Swal.fire({
                                                icon: "warning",
                                                title: "Invalid Quantity",
                                                text: `Overall Handover (${alreadyHandovered + val}) cannot exceed Issued Quantity (${issuedQty})`,
                                            });
                                            return;
                                        }

                                        setEditedQuantities((prev) => ({
                                            ...prev,
                                            [record.issueLineId]: val ?? 0,
                                        }));

                                        setTouchedLines((prev) => {
                                            const updated = new Set(prev);
                                            updated.add(record.issueLineId);
                                            return updated;
                                        });
                                    }}
                                />
                            );
                        },
                    },
                ]
                : []),

        ];

        return baseColumns;
    };

    const dataURLToFile = (dataUrl: string, fileName: string): File => {
        const arr = dataUrl.split(",");
        const mimeMatch = arr[0].match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : "image/png";
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], fileName, { type: mime });
    };

    const handleUpdateHandover = () => {
        setIsEditMode(true);
        setShowSubmitBtm(true);

        const initialQuantities: Record<string, number> = {};
        detailsData?.lines?.forEach((line: any) => {
            // initialQuantities[line.issueLineId] = Number(line.quantityHandovered || 0);
            initialQuantities[line.issueLineId] = 0;
        });
        setEditedQuantities(initialQuantities);
    };

    const exitEditMode = () => {
        setIsEditMode(false);
        setShowSubmitBtm(false);
        setDisableSubmit(false);
        setEditedQuantities({});
        setTouchedLines(new Set());
    };

    const handleSubmitChanges = () => {
        if (!handoverSignature?.image) {
            Swal.fire({
                title: "Signature Required",
                text: "Signature is mandatory to submit handover changes.",
                icon: "warning",
                confirmButtonText: "OK"
            });
            setDisableSubmit(false);
            return;
        }

        setShowSubmitModal(true);
        setDisableSubmit(false);
    };

    const handleConfirmSubmit = async (handoverNumber: string, selectedUsers: any[]) => {
        try {
            setShowSubmitModal(false);

            const lines = detailsData?.lines || [];
            const errors: string[] = [];
            const handoverLines = [];
            let hasZeroError = false;
            let hasExceededError = false;

            for (const line of lines) {
                if (!touchedLines.has(line.issueLineId)) continue;

                const editedQty = editedQuantities[line.issueLineId];
                const issuedQty = Number(line.quantityIssued || 0);
                const alreadyHandoveredQty = Number(line.quantityHandovered || 0);

                if (editedQty === undefined) continue;

                if (editedQty <= 0) {
                    hasZeroError = true;
                }

                const totalHandoverQty = alreadyHandoveredQty + editedQty;

                if (totalHandoverQty > issuedQty) {
                    hasExceededError = true;
                    continue;
                }

                const issueTasksByIdLine: any[] = Array.isArray(line?.materialIssueTasks)
                    ? line.materialIssueTasks
                    : [];
                const tasksForLine = (lineHandoverTasks[line.issueLineId] || [])
                    .filter(
                        (t: any) =>
                            t.selected === true &&
                            t.issueTaskId != null &&
                            t.handoverQty != null &&
                            t.handoverQty !== "" &&
                            Number(t.handoverQty) > 0
                    )
                    .map((t: any) => {
                        const source = issueTasksByIdLine.find(
                            (s: any) => s.issueTaskId === t.issueTaskId
                        );
                        return {
                            issueTaskId: Number(t.issueTaskId),
                            taskId: source?.taskId != null ? Number(source.taskId) : null,
                            taskBoqId:
                                source?.taskBoqId != null ? Number(source.taskBoqId) : null,
                            handoverQty: Number(t.handoverQty),
                        };
                    });

                const handoverLineEntry: any = {
                    inventoryId: line.inventoryId,
                    issueLineId: line.issueLineId,
                    materialId: line.materialId,
                    categoryId: line.categoryId,
                    uomId: line.uomId,
                    uomType: line.uomType,
                    handoverQty: editedQty,
                };
                if (tasksForLine.length > 0) {
                    handoverLineEntry.tasks = tasksForLine;
                }
                handoverLines.push(handoverLineEntry);
            }

            if (hasZeroError) {
                Swal.fire({
                    title: "Validation Error",
                    text: "Handover quantity must be greater than 0 for selected materials",
                    icon: "error",
                });
                return;
            }

            if (hasExceededError) {
                Swal.fire({
                    title: "Validation Error",
                    text: "Total handover quantity cannot exceed issued quantity",
                    icon: "error",
                });
                return;
            }

            if (errors.length) {
                Swal.fire({
                    title: "Validation Error",
                    html: errors.join("<br/>"),
                    icon: "error",
                });
                return;
            }

            const formData = new FormData();
            if (handoverLines.length === 0) {
                Swal.fire({
                    icon: "warning",
                    title: "No Valid Changes",
                    text: "Please enter Handover Quantity greater than 0 for at least one material.",
                });
                return;
            }
            const handoverRequest: any = {
                materialIssueId: Number(id),
                // handoverNumber: hasSequence ? detailsData?.header?.handoverNumber : handoverNumber,
                handoverTo: Number(selectedUsers[0]),
                handoverLines,
                docKeys: [{
                    fileIndex: 0,
                    module: "Handover",
                    moduleFileType: "Signature",
                }],
            };

            if (numberingMode === 'manual') {
                handoverRequest.handoverNumber = handoverNumber;
            }

            handoverRequest.handoverLines = handoverLines;

            formData.append("handoverRequest", JSON.stringify(handoverRequest));

            if (handoverSignature?.image) {
                const signatureFile = dataURLToFile(handoverSignature.image, "signature.png");
                formData.append("files", signatureFile);
            }

            const confirm = await Swal.fire({
                title: "Are you sure?",
                text: "Do you want to submit handover changes?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, Submit",
                cancelButtonText: "Cancel",
            });

            if (!confirm.isConfirmed) return;

            const response = await inventoryCreation.updateHandoverQuantities(formData);

            if (response?.status) {
                Swal.fire("Success", "Handover updated successfully", "success");
                await fetchPendingMaterialDetails(id);
                exitEditMode();
            } else {
                Swal.fire("Error", "Update failed", "error");
                exitEditMode();
            }
        } catch (err) {
            Swal.fire("Error", "Something went wrong", "error");
            exitEditMode();
        }
    };

    const handleRevertChanges = () => {
        Swal.fire({
            title: "Are you sure?",
            text: "Do you want to revert the changes?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, revert it!",
            cancelButtonText: "No, cancel",
        }).then(async (result) => {
            if (result.isConfirmed) {
                await fetchPendingMaterialDetails(id);
                setIsEditMode(false);
                setShowSubmitBtm(false);
                setEditedQuantities({});
                Swal.fire("Reverted!", "Your changes have been reverted.", "success");
            }
        });
    };

    const SubmitHandoverModal = ({ isModalVisible, setIsModalVisible, onConfirm, hasSequence }: any) => {
        const [handoverNumber, setHandoverNumber] = useState("");
        const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
        const [isSubmitting, setIsSubmitting] = useState(false);

        // Auto-generate number when in auto mode
        useEffect(() => {
            if (numberingMode === 'auto' && currentSiteSequence && !handoverNumber) {
                const { prefix } = currentSiteSequence;
                setHandoverNumber(`${prefix}`);
            }
        }, [numberingMode, currentSiteSequence, handoverNumber]);


        const handleSubmit = () => {
            if (numberingMode === 'manual' && !handoverNumber.trim()) {
                Swal.fire({
                    title: "Handover Number Required",
                    text: "Please enter a handover number",
                    icon: "warning",
                    confirmButtonText: "OK"
                });
                return;
            }

            if (selectedUsers.length === 0) {
                Swal.fire({
                    title: "Users Required",
                    text: "Please select at least one user",
                    icon: "warning",
                    confirmButtonText: "OK"
                });
                return;
            }

            setIsSubmitting(true);
            onConfirm(handoverNumber, selectedUsers);
        };

        const handleCancel = () => {
            setIsModalVisible(false);
            setHandoverNumber("");
            setSelectedUsers([]);
        };

        return (
            <AntModal open={isModalVisible} footer={null} onCancel={handleCancel} width={600}>
                <div className="mt-2">
                    <h6 style={{ marginBottom: "20px" }}>Submit Material Handover</h6>
                    <div className="mb-3">
                        {numberingMode === 'manual' ? (
                            <div style={{ marginBottom: "16px", position: 'relative' }}>
                                <label className="form-label">Material Handover Number</label>
                                <Input
                                    value={handoverNumber}
                                    onChange={(e) => setHandoverNumber(e.target.value)}
                                    placeholder="Enter handover number"
                                    required
                                />
                                <Tooltip title="Click here to enable or disable auto-generation of handover numbers.">
                                    <SettingOutlined
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50px',
                                            transform: 'translateY(-50%)',
                                            cursor: 'pointer',
                                            fontSize: '18px',
                                            color: '#1890ff',
                                            zIndex: 1,
                                        }}
                                        onClick={() => {
                                            setTempNumberingMode(numberingMode);
                                            setShowSubmitModal(false);
                                            setShowNumberingModal(true);
                                        }}
                                    />
                                </Tooltip>
                            </div>
                        ) : (
                            // In auto mode, show disabled field with settings icon
                            <div style={{ marginBottom: "16px", position: 'relative' }}>
                                <label className="form-label">Material Handover Number</label>
                                <Input
                                    value={handoverNumber}
                                    placeholder="Auto-generated"
                                    disabled
                                />
                                <Tooltip title="Click here to enable or disable auto-generation of handover numbers.">
                                    <SettingOutlined
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50px',
                                            transform: 'translateY(-50%)',
                                            cursor: 'pointer',
                                            fontSize: '18px',
                                            color: '#1890ff',
                                            zIndex: 1,
                                        }}
                                        onClick={() => {
                                            setTempNumberingMode(numberingMode);
                                            setShowSubmitModal(false);
                                            setShowNumberingModal(true);
                                        }}
                                    />
                                </Tooltip>
                            </div>
                        )}

                        <div style={{ marginBottom: "16px" }}>
                            <label className="form-label">Handover to User</label>
                            <Select
                                style={{ width: "100%" }}
                                placeholder="Select user"
                                value={selectedUsers[0]}
                                onChange={(val) => setSelectedUsers([val])}
                                options={usersList}
                                loading={loadingUsers}
                            />
                        </div>
                    </div>
                    <div className="actions clearfix">
                        <ul style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', margin: 0, padding: 0 }}>
                            <li>
                                {isSubmitting ? (
                                    <Button color="primary" type="button">
                                        <Spinner size="sm" color="light" />
                                    </Button>
                                ) : (
                                    <Button
                                        color="primary"
                                        onClick={handleSubmit}
                                        disabled={(numberingMode === 'manual' && !handoverNumber.trim()) || selectedUsers.length === 0}
                                    >
                                        Submit
                                    </Button>
                                )}
                            </li>
                            <li>
                                <Button color="primary" onClick={handleCancel} className="btn-dim btn btn-secondary" type="button">
                                    Cancel
                                </Button>
                            </li>
                        </ul>
                    </div>
                </div>
            </AntModal>
        );
    };

    return (
        <>
            <Head title="Material Issue Details" />
            <Content>
                <Card className="card-bordered">
                    <div className="card-aside-wrap">
                        {isLoading ? (
                            <Flex align="center" gap="middle" className="pageLoader">
                                <Spin size="large" className="m-auto" />
                            </Flex>
                        ) : (
                            <div className="card-inner card-inner-lg">
                                <BlockHead size="lg">
                                    <BlockBetween>
                                        <BlockHeadContent>
                                            <BlockTitle tag="h4">
                                                <span>Material Issue Details # - {detailsData?.header?.issueNumber} | {detailsData?.header?.issueStatus}</span>
                                            </BlockTitle>
                                        </BlockHeadContent>
                                        <div className="d-flex">
                                            {canUpdateHandover && showSubmitBtm ? (
                                                <div className="d-flex align-items-center">
                                                    <div className="actions clearfix paddingLeft10">
                                                        <ul className="m-auto d-flex align-items-center justify-content-center p-0" style={{ flexDirection: "row-reverse" }}>
                                                            <li className="pt-0 pb-0">
                                                                <Button color="primary" type="button" onClick={() => { if (disableSubmit) return; setDisableSubmit(true); handleSubmitChanges(); }}>
                                                                    Save Changes
                                                                </Button>
                                                            </li>
                                                            <li className="p-0">
                                                                <Button color="secondary" onClick={handleRevertChanges} type="button" className="btn-dim btn btn-secondary">
                                                                    Revert Changes
                                                                </Button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                    <div className="paddingLeft10">
                                                        <Button className="toggle" color="primary" onClick={() => navigate("/material-handover-pending")}>
                                                            <Icon name="arrow-left" />
                                                            <span>Back</span>
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="d-flex align-items-center">
                                                    <div className="paddingLeft10">
                                                        <Button className="toggle" color="primary" onClick={() => navigate("/material-handover-pending")}>
                                                            <Icon name="arrow-left" />
                                                            <span>Back</span>
                                                        </Button>
                                                    </div>
                                                    {canUpdateHandover && !showSubmitBtm && (
                                                        <ul className="nk-tb-actions ms-2">
                                                            <li>
                                                                <UncontrolledDropdown>
                                                                    <DropdownToggle tag="a" className="dropdown-toggle btn btn-icon btn-trigger">
                                                                        <Icon name="more-v"></Icon>
                                                                    </DropdownToggle>
                                                                    <DropdownMenu end>
                                                                        <ul className="link-list-opt no-bdr">
                                                                            <li onClick={(e) => { e.preventDefault(); handleUpdateHandover(); }}>
                                                                                <Link to={`#`}>
                                                                                    <Icon name="edit" /> Update Handover
                                                                                </Link>
                                                                            </li>
                                                                        </ul>
                                                                    </DropdownMenu>
                                                                </UncontrolledDropdown>
                                                            </li>
                                                        </ul>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </BlockBetween>
                                </BlockHead>
                                <div className="content clearfix">
                                    <AntCollapse defaultActiveKey={["1", "2"]}>
                                        <Panel header="General Information" key="1">
                                            <div>
                                                <Col className="justify-start w-100 flex-wrap p-3">
                                                    <div className="w-50 border-end">
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Site</div>
                                                            <div className="data-value w-70">{detailsData?.header?.siteName || "NA"}</div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Contractor</div>
                                                            <div className="data-value w-70">{detailsData?.header?.contractorName || "NA"}</div>
                                                        </div>
                                                    </div>
                                                    <div className="w-50 paddingLeft10">
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Issue Status</div>
                                                            <div className="data-value w-70">{detailsData?.header?.issueStatus || "NA"}</div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Handover Status</div>
                                                            <div className="data-value w-70">{detailsData?.header?.handoverStatus || "NA"}</div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Creation Date</div>
                                                            <div className="data-value w-70">
                                                                {dayjs(detailsData?.header?.createdAt).format("DD-MMM-YYYY") || "NA"}
                                                            </div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Created By</div>
                                                            <div className="data-value w-70">{detailsData?.header?.createdUserName || "NA"}</div>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </div>
                                        </Panel>
                                        <Panel header="Material Information" key="2">
                                            <div className="table-responsive poTable">
                                                <div style={{ width: screenSize - 350 + "px", margin: "0px auto 15px auto" }}>
                                                    <Table
                                                        className="customTable"
                                                        columns={getColumns()}
                                                        dataSource={detailsData?.lines?.map((item: any) => ({ ...item, key: item?.issueLineId }))}
                                                        scroll={{ x: 1000 }}
                                                        bordered
                                                        size="middle"
                                                        pagination={false}
                                                        expandable={{
                                                            expandedRowRender: (record: any) => {
                                                                const lineKey = record.issueLineId;
                                                                const editable = canUpdateHandover && isEditMode;
                                                                const issueTasks: any[] = Array.isArray(record?.materialIssueTasks)
                                                                    ? record.materialIssueTasks
                                                                    : [];
                                                                if (!editable) {
                                                                    return (
                                                                        <MaterialTaskPanel
                                                                            tasks={issueTasks}
                                                                            readOnly
                                                                        />
                                                                    );
                                                                }
                                                                if (issueTasks.length === 0) {
                                                                    return (
                                                                        <div style={{ padding: "12px 16px", color: "#8c8c8c", fontStyle: "italic" }}>
                                                                            No tasks for this line. Use the header Handover Quantity field.
                                                                        </div>
                                                                    );
                                                                }
                                                                const stateTasks = lineHandoverTasks[lineKey] || [];
                                                                const lineIssuedQty = Number(record?.quantityIssued || 0);
                                                                const lineAlreadyHandovered = Number(record?.quantityHandovered || 0);
                                                                const allSelected =
                                                                    stateTasks.length > 0 &&
                                                                    stateTasks.every((t: any) => t.selected);
                                                                const someSelected = stateTasks.some(
                                                                    (t: any) => t.selected
                                                                );
                                                                const editColumns = [
                                                                    {
                                                                        title: (
                                                                            <Checkbox
                                                                                checked={allSelected}
                                                                                indeterminate={!allSelected && someSelected}
                                                                                onChange={(e) => {
                                                                                    const checked = e.target.checked;
                                                                                    setLineHandoverTasks((prev) => ({
                                                                                        ...prev,
                                                                                        [lineKey]: (prev[lineKey] || []).map((t: any) => ({
                                                                                            ...t,
                                                                                            selected: checked,
                                                                                            handoverQty: checked
                                                                                                ? t.handoverQty
                                                                                                : undefined,
                                                                                        })),
                                                                                    }));
                                                                                }}
                                                                            />
                                                                        ),
                                                                        key: "selected",
                                                                        width: 44,
                                                                        align: "center" as const,
                                                                        render: (_: any, row: any) => {
                                                                            const stateEntry = stateTasks.find(
                                                                                (t: any) => t.issueTaskId === row.issueTaskId
                                                                            );
                                                                            return (
                                                                                <Checkbox
                                                                                    checked={!!stateEntry?.selected}
                                                                                    onChange={(e) => {
                                                                                        const checked = e.target.checked;
                                                                                        if (!stateEntry) return;
                                                                                        updateHandoverTask(lineKey, stateEntry.key, {
                                                                                            selected: checked,
                                                                                            handoverQty: checked
                                                                                                ? stateEntry.handoverQty
                                                                                                : undefined,
                                                                                        });
                                                                                    }}
                                                                                />
                                                                            );
                                                                        },
                                                                    },
                                                                    {
                                                                        title: "#",
                                                                        key: "index",
                                                                        width: 44,
                                                                        align: "center" as const,
                                                                        render: (_: any, __: any, idx: number) => idx + 1,
                                                                    },
                                                                    {
                                                                        title: "Task",
                                                                        key: "taskName",
                                                                        render: (_: any, row: any) =>
                                                                            row?.taskName ||
                                                                            (row?.taskId != null ? `Task #${row.taskId}` : "--"),
                                                                    },
                                                                    {
                                                                        title: "BOQ Line",
                                                                        dataIndex: "boqLineName",
                                                                        key: "boqLineName",
                                                                        render: (val: any) => val || "--",
                                                                    },
                                                                    {
                                                                        title: "Issued Qty",
                                                                        dataIndex: "quantityIssued",
                                                                        key: "quantityIssued",
                                                                        align: "right" as const,
                                                                        render: (val: any) => {
                                                                            const n = Number(val);
                                                                            return Number.isNaN(n) ? (val ?? "--") : n.toLocaleString("en-IN");
                                                                        },
                                                                    },
                                                                    {
                                                                        title: "Handover Status",
                                                                        dataIndex: "handoverStatus",
                                                                        key: "handoverStatus",
                                                                        render: (val: any) => val || "--",
                                                                    },
                                                                    {
                                                                        title: "Handovered Qty",
                                                                        key: "handoverQty",
                                                                        width: 160,
                                                                        render: (_: any, row: any) => {
                                                                            const stateEntry = stateTasks.find(
                                                                                (t: any) => t.issueTaskId === row.issueTaskId
                                                                            );
                                                                            const taskIssuedQty = Number(row?.quantityIssued || 0);
                                                                            const taskAlreadyHandovered = Number(row?.quantityHandovered || 0);
                                                                            const isSelected = !!stateEntry?.selected;
                                                                            return (
                                                                                <InputNumber
                                                                                    min={0}
                                                                                    step={0.001}
                                                                                    placeholder={isSelected ? "Qty" : "Select task"}
                                                                                    style={{ width: "100%" }}
                                                                                    value={stateEntry?.handoverQty}
                                                                                    disabled={!isSelected}
                                                                                    onChange={(val) => {
                                                                                        if (val === null) {
                                                                                            if (stateEntry) {
                                                                                                updateHandoverTask(lineKey, stateEntry.key, { handoverQty: undefined });
                                                                                            }
                                                                                            return;
                                                                                        }
                                                                                        const numVal = Number(val);
                                                                                        if (numVal < 0) return;
                                                                                        if (taskIssuedQty > 0 && taskAlreadyHandovered + numVal > taskIssuedQty) {
                                                                                            Swal.fire({
                                                                                                icon: "warning",
                                                                                                title: "Invalid Quantity",
                                                                                                text: `Task Handover (${taskAlreadyHandovered + numVal}) cannot exceed Task Issued Qty (${taskIssuedQty}).`,
                                                                                            });
                                                                                            return;
                                                                                        }
                                                                                        const otherTaskSum = stateTasks.reduce(
                                                                                            (acc: number, t: any) => {
                                                                                                if (t.issueTaskId === row.issueTaskId) return acc;
                                                                                                return acc + (t?.handoverQty != null ? Number(t.handoverQty) : 0);
                                                                                            },
                                                                                            0
                                                                                        );
                                                                                        if (lineIssuedQty > 0 && lineAlreadyHandovered + otherTaskSum + numVal > lineIssuedQty) {
                                                                                            Swal.fire({
                                                                                                icon: "warning",
                                                                                                title: "Invalid Quantity",
                                                                                                text: `Line Handover total (${lineAlreadyHandovered + otherTaskSum + numVal}) cannot exceed Line Issued Qty (${lineIssuedQty}).`,
                                                                                            });
                                                                                            return;
                                                                                        }
                                                                                        if (stateEntry) {
                                                                                            updateHandoverTask(lineKey, stateEntry.key, { handoverQty: numVal });
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            );
                                                                        },
                                                                    },
                                                                ];
                                                                return (
                                                                    <div style={{ padding: "4px 8px 12px" }}>
                                                                        <div style={{ fontWeight: 600, marginBottom: 12 }}>
                                                                            Task-wise Handover
                                                                        </div>
                                                                        <Table
                                                                            columns={editColumns}
                                                                            dataSource={issueTasks}
                                                                            rowKey={(row: any) => row.issueTaskId}
                                                                            pagination={false}
                                                                            size="small"
                                                                            bordered
                                                                            tableLayout="fixed"
                                                                            style={{ background: "#fff", borderRadius: 4 }}
                                                                        />
                                                                    </div>
                                                                );
                                                            },
                                                            rowExpandable: () => true,
                                                        }}
                                                    />
                                                </div>

                                                {canUpdateHandover && isEditMode && (
                                                    <Row className="m-4 justify-content-end">
                                                        {handoverSignature?.isActive ? (
                                                            <div className="signatureBtnWrapper">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <strong>Signature</strong>
                                                                    <a style={{ fontSize: "14px", cursor: "pointer" }} onClick={() => {
                                                                        setHandoverSignature((prev: any) => ({ ...prev, isActive: false, image: null, label: "Authorised Signatory" }));
                                                                    }}>
                                                                        <Icon name="cross" className="pointer" />
                                                                    </a>
                                                                </div>
                                                                <div style={{ minHeight: handoverSignature?.image ? "0px" : "120px", display: handoverSignature?.image ? "block" : "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                                                                    {!handoverSignature?.image ? (
                                                                        <>
                                                                            <Button color="btn-dim btn" onClick={() => setShowCropModel(true)} className="mb-2">
                                                                                <Icon name="upload-cloud" />
                                                                                <span>Upload</span>
                                                                            </Button>
                                                                            <div className="mt-2">
                                                                                <Button color="btn-dim btn" onClick={() => setShowSignatureModel(true)}>
                                                                                    <Icon name="pen" />
                                                                                    <span>Use Signature Pad</span>
                                                                                </Button>
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <div className="signatueImgWrapper border mb-3">
                                                                                <img src={handoverSignature?.image} alt="Signed" style={{ width: "160px" }} />
                                                                            </div>
                                                                            <div className="mb-3">
                                                                                <Button color="btn-dim btn" onClick={() => setHandoverSignature((pre: any) => ({ ...pre, image: null }))}>
                                                                                    <Icon name="redo" />
                                                                                    <span>Reset</span>
                                                                                </Button>
                                                                            </div>
                                                                            <div className="form-group w-100">
                                                                                <label className="form-label" htmlFor="signatureLabel">Signature Label</label>
                                                                                <input type="text" className="form-control" name="signatureLabel" onChange={(e) => {
                                                                                    setHandoverSignature((pre: any) => ({ ...pre, label: e.target.value }));
                                                                                }} defaultValue={handoverSignature?.label || "Authorised Signatory"} />
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div style={{ border: "1px solid #e5ecf7", backgroundColor: "#f7faff", padding: "10px", borderRadius: "5px", textAlign: "center", width: "200px" }}>
                                                                <Button color="btn-dim btn" onClick={() => {
                                                                    setHandoverSignature({ isActive: true, image: null, label: "Authorised Signatory" });
                                                                }} style={{ width: "100%" }}>
                                                                    <Icon name="edit" />
                                                                    <span>Add Signature</span>
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </Row>
                                                )}
                                            </div>
                                        </Panel>
                                    </AntCollapse>
                                    {showCropModel && <CropperComponent modal={showCropModel} toggle={() => setShowCropModel(!showCropModel)} onSave={(data: string) => {
                                        setHandoverSignature((pre: any) => ({ ...pre, image: data }));
                                        setShowCropModel(false);
                                    }} />}
                                    {canUpdateHandover && isEditMode && <SignatureModal modal={showSignatureModel} toggle={() => setShowSignatureModel(!showSignatureModel)} onSave={(data: string) => {
                                        setHandoverSignature((pre: any) => ({ ...pre, image: data }));
                                        setShowSignatureModel(false);
                                    }} />}
                                </div>
                            </div>
                        )}
                    </div>
                </Card>
            </Content>
            {showSubmitModal && <SubmitHandoverModal isModalVisible={showSubmitModal} setIsModalVisible={setShowSubmitModal} onConfirm={handleConfirmSubmit} hasSequence={hasSequence} />}

            {/* Handover Number Configuration Modal  */}
            <AntdModal
                title="Configure Handover Number Preference"
                open={showNumberingModal}
                onCancel={() => {
                    setShowNumberingModal(false);
                    setShowSubmitModal(true);
                }}
                footer={
                    <div style={{ display: "flex", justifyContent: "flex-start", gap: "10px" }}>
                        <Button
                            key="save"
                            color="primary"
                            onClick={() => {
                                if (tempNumberingMode === "auto" && !currentSiteSequence) {
                                    message.error("Auto-generation is not configured for this site.");
                                    return;
                                }

                                setNumberingMode(tempNumberingMode);
                                setShowNumberingModal(false);
                                setShowSubmitModal(true);
                            }}
                        >
                            Save
                        </Button>

                        <Button
                            key="cancel"
                            color="primary"
                            onClick={() => {
                                setShowNumberingModal(false);
                                setShowSubmitModal(true);
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                }
            >
                <div style={{ marginBottom: 20, lineHeight: "22px" }}>
                    Your handover numbers are currently set to auto-generate to save time.
                    <br />
                    Are you sure you want to change this setting?
                </div>

                {!siteId && (
                    <div style={{ color: "#8c8c8c", marginBottom: 16 }}>
                        <InfoCircleOutlined /> Sequence configuration is not set for this site. Please configure the sequence.
                    </div>
                )}

                {siteId && !currentSiteSequence && (
                    <div style={{ color: "#faad14", marginBottom: 16 }}>
                        <InfoCircleOutlined />{" "}
                        {activeSequences.length === 0
                            ? "No sequences configured for Handover. Please set up a sequence first."
                            : "No sequence configured for the selected site. Please set a sequence for this site."}
                    </div>
                )}

                <Radio.Group
                    onChange={(e) => setTempNumberingMode(e.target.value)}
                    value={tempNumberingMode}
                >
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Radio value="auto" disabled={!canAuto}>
                            <span style={{ fontWeight: 500 }}>
                                Continue auto-generating handover numbers
                            </span>
                        </Radio>

                        <Radio value="manual">
                            <span style={{ fontWeight: 500 }}>
                                Enter handover numbers manually
                            </span>
                        </Radio>
                    </Space>
                </Radio.Group>
            </AntdModal>
        </>
    );
};

export default MaterialHandoverPendingDetails;